import {
  keepPreviousData,
  useQuery,
  useInfiniteQuery,
  queryOptions
} from '@tanstack/react-query'
import { fetchEmployeeById, fetchUsersEmployees } from '../api/user.service.ts'
import type { PaginationModel, SortModel } from '@/components'
import type { UserEmployee } from '@/features/user/api/user-employee.data.ts'
import type { UserRoleType } from '@/features/user/api'

export interface UseUserEmployeeListOptions {
  sortModel?: SortModel<UserEmployee>
  paginationModel?: PaginationModel
  search?: string
  roles?: UserRoleType[]
  siteIds?: string[]
  statuses?: string[]
}

export function useEmployeeUserList({
  sortModel,
  paginationModel,
  search,
  roles,
  siteIds,
  statuses
}: UseUserEmployeeListOptions) {
  return useQuery({
    queryKey: ['employees', sortModel, paginationModel, search, roles, siteIds, statuses],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      fetchUsersEmployees(
        sortModel,
        paginationModel,
        search,
        roles,
        siteIds,
        statuses,
        signal
      )
  })
}

export const userQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: ['employees', userId],
    queryFn: async () => fetchEmployeeById(userId)
  })

export function useGetUser(userId: string) {
  return useQuery(userQueryOptions(userId))
}

export function useGetAllUsersByRole(
  role?: UserRoleType | null,
  listSiteIds?: string[],
  pageSize: number = 100
) {
  return useInfiniteQuery({
    queryKey: ['employees', 'all', 'role', role ?? null, listSiteIds ?? null, pageSize],
    queryFn: ({ pageParam = 0, signal }) =>
      fetchUsersEmployees(
        undefined,
        { page: pageParam, pageSize },
        undefined,
        role ? [role] : undefined,
        listSiteIds,
        undefined,
        signal
      ),
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length * pageSize >= (lastPage.totalItems ?? 0)) return undefined
      return allPages.length
    },
    initialPageParam: 0,
    select: data => {
      const flattened = data.pages.flatMap(page => page.content)
      if (!role) return flattened
      return flattened.filter(u => u.role === role)
    }
  })
}
